import React, { Component } from "react";
import { Container, Row, Col, Alert, ButtonGroup, Button } from "react-bootstrap";
import { GoX } from 'react-icons/go';
import Selector from "./selector/index";
import Workspace from "./workspace/index";
import Synchronizer from "./sync";
import SessionManager from "./session/manager";
import SessionContext from "./session/context";
import { isSessionUnavailable, sessionUnavailableMessage } from "../controls/common/sessionReference";
import Navigation from "context/nav";
import Preferences, { withPreferences } from 'context/preferences';

// The registry key, and the words the Blob page's confirmation uses to name this surface.
const UNSAVED_WORK = "the playground";

// Exported (named) for unit tests that inspect the raw class directly; the
// default export below is the withPreferences-wrapped component used by the app.
export class PlaygroundShell extends Component {
  constructor(props) {
    super(props);
    const scope = { orgId: this.props.org.id, blobId: this.props.blob.id };

    this.state = {
      /**
       * State
       */

      // Global loading success status (manifest, credentials, etc)
      fatal: false,
      fatalMessage: null,
      // Definition operations progress flags
      progress: false,
      progressMessage: null,
      error: false,
      errorMessage: null,

      /**
       * Workflow
       */

      // All available workflow definitions
      workflowDefinitions: null,

      /**
       * Playground
       */

      // Playground manifest
      manifest: null,
      // All available playground definitions
      definitions: null,
      // Selected playground ID
      definitionId: null,
      // Selected playground definition item
      definition: null,
      // Initial state of currently selected definition object
      definitionObjectBackup: null,
      // Flag indicating whether definition is locally modified
      modified: false,

      /**
       * Workspace
       */

      nodes: [],
      edges: [],

      /**
       * UX
       */

      library: {
        visible: false,
      },
      details: {
        visible: Preferences.get("blob:workflow.playgroundDetailsPane", scope),
        view: null,
        data: null
      },
      wideContent: Preferences.get("blob:workflow.playgroundWideContent", scope),

      // Workspace zoom factor (1.0 = 100%). Applies to both ReactFlow's
      // viewport in free-form and to the GridLayout content scaling in
      // grid mode. Not persisted.
      zoom: 1,

      // Vertical sizing mode for grid playgrounds. One of: "pitch" (default —
      // row height is a fixed target pitch and the row count fills the viewport),
      // "fit" (row height derived so content rows fill the viewport exactly),
      // "fixed_rows" (row height derived from a fixed row count stored in
      // reference_rows). Live values shadow the persisted layout block; the
      // layout block is the source of truth on load.
      verticalSizing: "pitch",
      referenceRows: 12,

      /**
       * Interfaces
       */

      // ReactFlow control interface
      workflowInterface: {
        setNodes: this.setNodes,
        setEdges: this.setEdges
      },
      // Shell control interface
      shellInterface: {
        canEditRevision: this.canEditRevision,
        isSessionOpen: this.isSessionOpen,
        canMutateSession: this.canMutateSession,
        savePlayground: this.savePlayground,
        revertPlayground: this.revertPlayground,
        markModified: this.markModified,
        selectSession: this.selectSession,
        sessionSelected: this.sessionSelected,
        getState: this.getState,
        updateState: this.updateState,
        updatePane: this.updatePane,
        updateProgress: this.updateProgress,
        updateDetails: this.updateDetails,
        getApp: this.getApp,
        getBlob: this.getBlob,
        getRevision: this.getRevision,
        getScope: this.getScope,
        getSync: this.getSync,
        createWorkflowPath: this.createWorkflowPath,
        enableSessionSync: this.enableSessionSync,
        switchDefinition: this.switchDefinition,
        syncDefinitionPath: this.syncDefinitionPath,
        reloadDefinitions: this.reloadDefinitions,
        setZoom: this.setZoom,
        setVerticalSizing: this.setVerticalSizing,
        setReferenceRows: this.setReferenceRows,
        toggleWideContent: this.toggleWideContent,
      },
    };

    this.sync = null;
  }

  /**
   * Shell interface
   */

  canEditRevision = () => {
    return this.props.app.api.canUpdateRevision(this.props.blob, this.props.revision);
  }

  // Whether the currently selected session accepts mutations. Distinct from
  // canEditRevision (revision write access + draft/managed phase): a session
  // that is closing/closed/deleting is read-only regardless of revision
  // editability. This does NOT affect editing the workflow definition/layout
  // itself — that stays gated on canEditRevision alone (see markModified,
  // setZoom, setVerticalSizing, setReferenceRows below, and the workspace
  // isModified checks), since a closed session has no bearing on the
  // playground's own definition.
  isSessionOpen = () => {
    return !!(this.sessionManager && this.sessionManager.isSessionOpen());
  }

  // Combined seam for gating session-data mutations (new/edit/delete session
  // objects, thread posts, graph mutations, new executions, ...): both an
  // editable revision AND an open session are required.
  canMutateSession = () => {
    return this.canEditRevision() && this.isSessionOpen();
  }

  savePlayground = () => {
    this.sync.savePlayground();
  }

  revertPlayground = () => {
    this.sync.revertPlayground();
  }

  markModified = () => {
    this.setState({ modified: true });
  }

  setZoom = (zoom) => {
    const next = Math.max(0.5, Math.min(2, Number(zoom) || 1));
    // No-op if the value hasn't changed; this guards the bidirectional
    // sync loop with ReactFlow's onViewportChange (slider -> setZoom ->
    // useEffect -> zoomTo -> onViewportChange -> setZoom).
    if (Math.abs((this.state.zoom ?? 1) - next) < 0.001) return;
    // Zoom is part of the persisted layout block, so a user-initiated
    // zoom change should mark the playground as modified to surface the
    // Save button. The load path uses updateState({zoom}) directly to
    // bypass this; programmatic free-form viewport changes (fitView,
    // zoomTo) are filtered out at the onMoveEnd handler.
    // Read-only viewers can still pan/zoom ReactFlow's viewport, so the
    // value mirrors into shell state, but the playground is never marked
    // modified for them — they have no Save button anyway.
    const markModified = this.canEditRevision();
    this.setState({ zoom: next, ...(markModified ? { modified: true } : {}) });
  }

  setVerticalSizing = (value) => {
    const allowed = ["pitch", "fit", "fixed_rows"];
    const next = allowed.includes(value) ? value : "pitch";
    if (this.state.verticalSizing === next) return;
    const markModified = this.canEditRevision();
    this.setState({ verticalSizing: next, ...(markModified ? { modified: true } : {}) });
  }

  setReferenceRows = (intValue) => {
    const next = Math.max(1, Math.min(128, parseInt(intValue, 10) || 12));
    if (this.state.referenceRows === next) return;
    const markModified = this.canEditRevision();
    this.setState({ referenceRows: next, ...(markModified ? { modified: true } : {}) });
  }

  toggleWideContent = () => {
    const next = !this.state.wideContent;
    const scope = { orgId: this.props.org.id, blobId: this.props.blob.id };
    Preferences.set("blob:workflow.playgroundWideContent", next, scope);
    this.setState({ wideContent: next });
  }

  /**
   * Applies session selection based on URL upon initial page loading
   */
  selectSession = () => {
    // Skip if session is already selected
    if ( null != this.state.sessionId ) {
      return;
    }

    // Check the path
    const section = this.props.match.params.section;
    const sessionId = this.props.match.params.subSectionId;

    // If URL contains a session ID, find and select it
    if ( "playground" == section && sessionId ) {
      let requestedSession = null;
      if (this.sessionManager.sessions) {
        for ( const session of this.sessionManager.sessions ) {
          if ( session["id"] == sessionId ) {
            requestedSession = session;
            break;
          }
        }
      }

      if ( null == requestedSession ) {
        return this.resolveLinkedSession(sessionId);
      }

      // Select session
      this.sessionSelected(requestedSession);
      return;
    }

    // Auto-select most recent session if preference is enabled
    if ("playground" == section && this.state.definition) {
      const scope = { orgId: this.props.org.id, blobId: this.props.blob.id };
      const autoSelect = Preferences.get("blob:workflow.autoSelectRecentSession", scope);
      if (autoSelect && this.sessionManager.sessions && this.sessionManager.sessions.length > 0) {
        this.sessionSelected(this.sessionManager.sessions[0]);
      }
    }
  }

  // A session named in the URL that the loaded page of the list does not carry. The miss is not an
  // answer -- the list is one page of ten -- so the API is asked, and the manager may have been nulled
  // by an unmount under this read. docs/specs/2026-09-18-revision-selection.md#a-session-link-that-points-elsewhere
  resolveLinkedSession = (sessionId) => {
    return this.sessionManager.resolveSession(sessionId).then(result => {
      if ( !this.sessionManager ) {
        return;
      }

      if ( result.session ) {
        this.sessionSelected(result.session);
        return;
      }

      // A refusal and a server that is down are different things to be told. Only the first is about
      // the link; reporting the second as one would send the reader off to hunt a URL that is fine.
      const message = isSessionUnavailable(result.error)
        ? sessionUnavailableMessage(sessionId)
        : `Failed to resolve session ${sessionId}: ` + result.error.message;
      this.updateProgress(false, null, true, message);
    });
  }

  /**
   * Reacts to events published on the session manager's listener contract. Most notifications are
   * argument-less "something changed" ticks handled by the forceUpdate listener registered in
   * componentDidMount; only session_deleted needs specific handling here.
   */
  handleSessionManagerEvent = (event) => {
    if ( event && "session_deleted" === event.type ) {
      this.sessionDeleted();
    }
  }

  /**
   * The selected session was deleted (server-side, observed on the session-events poller). The
   * manager has already cleared its own session, but this shell keeps an independent copy in
   * state.sessionId and the URL still names the deleted session. Leaving it stale means
   * switchDefinition keeps building URLs that point at a session that no longer exists, and
   * selectSession's `null != this.state.sessionId` early-return suppresses auto-select-most-recent.
   * Clear the copy and drop the session out of the URL, staying on the current playground.
   */
  sessionDeleted = () => {
    if ( null == this.state.sessionId ) {
      return;
    }

    const sectionId = this.state.definition ? this.state.definition["alias"] : null;
    this.setState({ sessionId: null });
    Navigation.push(this.props.history, this.createWorkflowPath("playground", sectionId));
  }

  sessionSelected = (session) => {
    // Update URL
    const sectionId = this.state.definition["alias"];
    const subSectionId = session["id"];
    Navigation.push(this.props.history, this.createWorkflowPath("playground", sectionId, subSectionId));

    // Track the selection in shell state too so subsequent navigations
    // (e.g. switching playgrounds) can include the session id in the URL.
    this.setState({ sessionId: subSectionId });

    // Disable sync if active
    if (this.sessionManager.sessionSync) {
      this.enableSessionSync(false);
    }

    // Begin the transition
    this.sessionManager.clearSessionState(session);
    return this.sessionManager.loadSessionContent()
      .then(() => {
        this.forceUpdate();
      });
  }

  updateState = (updates) => {
    const promise = new Promise((resolve, reject) => {
      this.setState(updates, () => {
        resolve();
      });
    });
    return promise;
  }

  getState = () => {
    return this.state;
  }

  updateProgress = (progress, progressMessage, error, errorMessage) => {
    this.setState({
      progress: progress,
      progressMessage: progressMessage,
      error: error,
      errorMessage: errorMessage
    });
  }

  updatePane = (pane, visible) => {
    let paneObject = this.state[pane];
    paneObject.visible = visible;
    this.setState({pane: paneObject});
  }

  updateDetails = (view, data) => {
    let details = this.state.details;
    if ( details.view == view && details.data == data ) {
      return;
    }

    details.view = view;
    details.data = data;
    this.setState({
      details: details
    });
  }

  getRevision = () => {
    return this.props.revision;
  }

  getBlob = () => {
    return this.props.blob;
  }

  getApp = () => {
    return this.props.app;
  }

  getScope = () => {
    return {
      orgId: this.props.org && this.props.org.id,
      blobId: this.props.blob && this.props.blob.id
    };
  }

  getSync = () => {
    return this.sync;
  }

  createWorkflowPath = (section, sectionId, subSectionId) => {
    const orgId = this.props.match.params.orgId;
    const blobId = this.props.match.params.blobId;
    return Navigation.workflowPath(orgId, blobId, section, sectionId, subSectionId);
  }

  enableSessionSync = (enable) => {
    this.sessionManager.enableSessionSync(enable);
    this.forceUpdate();
  }

  // Keep the URL pointing at whichever playground is actually selected.
  //
  // switchDefinition() pushes the path when the USER picks one, but every other way the selection
  // moves goes through sync.loadPlaygroundDefinitions(), which resolved a definition into state and
  // left the URL alone — so creating, renaming, or deleting a playground left the address bar naming
  // the old (or deleted) alias. Mirrors WorkflowShell.syncDefinitionPath.
  //
  // Replaces rather than pushes: these are corrections to the current location, not navigations the
  // user should have to walk back through. No-ops when the path already names this definition.
  //
  // Only ever a correction while the playground is the tab on screen. All three workflow tabs stay
  // mounted at once and merely hide with CSS, so this shell resolves its definition on every workflow
  // blob — including when the user is on Workflow, Revisions, or Settings. The path carries the URL
  // section the Blob page derives the active tab from, so rewriting it from a hidden tab does not
  // correct an alias, it throws the user onto the Playground tab. componentDidUpdate re-runs the sync
  // when the tab does become visible, so nothing is lost by deferring.
  syncDefinitionPath = (definition) => {
    if ( !this.props.shown ) {
      return;
    }
    if ( !definition || !definition["alias"] ) {
      return;
    }
    if ( this.props.match.params.sectionId === definition["alias"] ) {
      return;
    }
    this.props.history.replace(
      this.createWorkflowPath("playground", definition["alias"], this.state.sessionId));
  }

  switchDefinition = (definition) => {
    // Navigate to definition path
    const orgId = this.props.match.params.orgId;
    const blobId = this.props.match.params.blobId;
    const sectionId = definition["alias"];
    const subSectionId = this.state.sessionId;
    Navigation.push(this.props.history, this.createWorkflowPath("playground", sectionId, subSectionId));

    // Notify the view that new definition is selected and is about to be loaded
    this.setState({
      definition: definition,
      definitionId: definition["id"],
      // Reset selection so the details pane returns to the Playground
      // view; the previously selected component belongs to the old
      // playground and would no longer make sense.
      details: { ...this.state.details, view: null, data: null },
    }, () => {
      // Sessions are scoped to the revision, not the playground, so the
      // currently selected session and its data stay valid as we switch
      // playgrounds. Loading the new definition only refreshes nodes/edges.
      this.sync.loadPlaygroundDefinition();
    });
  }

  reloadDefinitions = (reference) => {
    // Reload the list
    this.sync.loadPlaygroundDefinitions(reference);
  }

  /**
   * Workflow interface
   */

  setNodes = (nodes, isModified) => {
    this.setState({
      nodes: nodes
    });

    if ( isModified ) {
      this.markModified();
    }
  }

  setEdges = (edges, isModified) => {
    this.setState({
      edges: edges
    });

    if ( isModified ) {
      this.markModified();
    }
  }

  /**
   * UX
   */

  componentDidMount() {
    this.sessionManager = new SessionManager(
        this.props.app, this.props.revision, this.updateProgress, () => this.forceUpdate());
    this.sync = new Synchronizer(this.props.app, this.props.revision, this.state, this.sessionManager);
    this.sync.mounted = true;
    this.sessionManager.mounted = true;
    // Forward the URL's playground alias (e.g. /playground/test-grid) so the
    // sync layer auto-selects that playground after loading definitions. Only when the URL is
    // actually addressing the playground: this shell mounts on every workflow blob, so on
    // /workflow/<alias> or /debugger/<session> the section id names something else entirely.
    const params = this.props.match?.params;
    const initialReference = ( "playground" === params?.section ) ? (params?.sectionId || null) : null;
    this.sync.loadPrerequisites(initialReference);
    this.sessionManager.autoRefreshSessionEvents();
    // Separate from the constructor's forceUpdate listener: that one re-renders on every tick and
    // ignores its argument, this one acts on specific lifecycle events (session_deleted).
    this.sessionManager.addListener(this.handleSessionManagerEvent);

    // Switching revision remounts this shell, which discards an unsaved layout block (zoom, vertical
    // sizing, reference rows, widget placement) that only savePlayground persists. Answering here is
    // what lets the Blob page ask before it does that.
    if ( this.props.unsavedWork ) {
      this.props.unsavedWork.register(UNSAVED_WORK, this.reportUnsavedWork);
    }

    document.addEventListener("keydown", this.handleKeyDown);
  }

  // The definition usually resolves while this tab is hidden (the shell is mounted on every workflow
  // blob), and syncDefinitionPath declines to touch the URL from there. Run the deferred sync the
  // moment the playground becomes the visible tab, so the address bar names the playground on screen
  // and a reload lands back on it.
  componentDidUpdate(prevProps) {
    if ( !prevProps.shown && this.props.shown ) {
      this.syncDefinitionPath(this.state.definition);
    }
  }

  componentWillUnmount() {
    if ( this.props.unsavedWork ) {
      this.props.unsavedWork.unregister(UNSAVED_WORK, this.reportUnsavedWork);
    }

    document.removeEventListener("keydown", this.handleKeyDown);

    this.sync.mounted = false;
    this.sync = null;
    this.sessionManager.removeListener(this.handleSessionManagerEvent);
    this.sessionManager.destroy();
    this.sessionManager = null;
  }

  // Read when the page needs the answer, never cached: `modified` is set from markModified, setZoom,
  // setVerticalSizing and setReferenceRows, and cleared from sync.js, so a pushed copy would go stale
  // at whichever site was forgotten.
  reportUnsavedWork = () => {
    return this.state.modified;
  }

  handleKeyDown = (event) => {
    // Shortcuts only fire while this tab is visible. The Editor/Debugger/
    // Playground shells are all mounted at once (WorkflowsShell toggles
    // visibility via CSS hidden) so the document-level listener stays
    // attached even on other tabs — gate on `shown` so the shortcut is
    // a no-op there and the browser default kicks in.
    if ( !this.props.shown ) return;

    // Command+S / Ctrl+S — save when modified and editable
    if ((event.metaKey || event.ctrlKey) && event.key === 's') {
      event.preventDefault();
      if (this.state.modified && this.canEditRevision()) {
        this.savePlayground();
      }
    }

    // Command+R / Ctrl+R — refresh the sessions list.
    // Cmd+Shift+R passes through to the browser for a force reload.
    if ((event.metaKey || event.ctrlKey) && !event.shiftKey && event.key === 'r') {
      if (!this.state.modified) {
        event.preventDefault();
        if (this.sessionManager) {
          this.sessionManager.loadSessions();
        }
      }
    }

    // Command+P / Ctrl+P — show/hide the properties panel.
    // preventDefault overrides the browser's print dialog.
    if ((event.metaKey || event.ctrlKey) && event.key === 'p') {
      event.preventDefault();
      this.updatePane("details", !this.state.details.visible);
    }
  }

  render() {
    const timezone = this.props.preferences["app.timezone"];

    if ( this.state.fatal ) {
      return (
        <>
          <Container className="blob-header-row content-row">
            <Alert variant="warning">
              {this.state.fatalMessage}
            </Alert>
          </Container>
        </>
      );
    }

    const selectorVisible
      = !this.state.progress
      && !this.props.headerCollapsed
      && null != this.state.manifest
      && null != this.state.definitions
      && null != this.sessionManager.sessions;

    const workspaceVisible
      = null != this.state.manifest
      && null != this.state.definition
      && null != this.state.definitionObjectBackup;

    // In collapsed mode the selector row is hidden and the surface fills the
    // pane, so progress/error alerts FLOAT over the top of the surface instead
    // of pushing it down (which grew the page and jumped the screen). See
    // `.blob-content-pane-floating` in Blob/index.css.
    const paneClass = "blob-content-pane" + (this.props.headerCollapsed ? " blob-content-pane-floating" : "");

    return (
      <SessionContext.Provider value={this.sessionManager}>
        {selectorVisible &&
          <Selector
            app={this.props.app}
            blob={this.props.blob}
            revision={this.props.revision}
            shell={this.state}
            sessionManager={this.sessionManager}
            timezone={timezone}
          />
        }

        <div className={paneClass}>
          {this.state.progress &&
            <Container className="blob-header-row content-row blob-pane-alert">
              <Alert variant="primary">
                {this.state.progressMessage}
              </Alert>
            </Container>
          }

          {this.state.error &&
            <Container className="blob-header-row content-row blob-pane-alert">
              <Alert variant="warning">
                {this.state.errorMessage}
                <span className="float-right">
                  <ButtonGroup size="sm" className="header-options">
                    <Button variant="light" className="btn-warning-close" onClick={(event) => {
                      this.updateProgress(false, null, false, null);
                    }}><GoX/></Button>
                  </ButtonGroup>
                </span>
              </Alert>
            </Container>
          }

          {workspaceVisible &&
            <Workspace
              app={this.props.app}
              blob={this.props.blob}
              revision={this.props.revision}
              shell={this.state}
              sessionManager={this.sessionManager}
              shown={this.props.shown}
              timezone={timezone}
            />
          }
        </div>
      </SessionContext.Provider>
    );
  }
}

export default withPreferences(PlaygroundShell, ["app.timezone"],
  props => ({ orgId: props.org.id, blobId: props.blob.id }));
